/**
 * General View Settings
 * Font sizes, multiple choice toggle, data import/export, and confetti
 */

import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import PillSelect from './PillSelect'
import { useSettingsStore, sanitizeLoadedSettings } from '@stores/settingsStore'
import { useSpeechStore, sanitizeLoadedSpeech } from '@stores/speechStore'
import { useStatsStore } from '@stores/statsStore'
import { exportData, importData } from '@lib/storage'
import { FONT_SIZE_OPTIONS, MASKED_API_KEY_PLACEHOLDER, TIMER } from '@config/constants'
import type { FontSize, Stats, ThemeOption, TimerOption } from '../../../types'

interface FontSettingsProps {
  isOpen: boolean
  onToggle: (open: boolean) => void
}

const IMPORT_STATUS_TIMEOUT_MS = 4000

export default function FontSettings({ isOpen, onToggle }: FontSettingsProps) {
  const settings = useSettingsStore(state => state.settings)
  const updateFontSizes = useSettingsStore(state => state.updateFontSizes)
  const updateCelebrations = useSettingsStore(state => state.updateCelebrations)
  const updateTimers = useSettingsStore(state => state.updateTimers)
  const updateTheme = useSettingsStore(state => state.updateTheme)
  const [expandedTimers, setExpandedTimers] = useState(false)
  const [expandedSection, setExpandedSection] = useState<'question' | 'answer' | 'theme' | null>(null)
  const [expandedData, setExpandedData] = useState(false)

  const speech = useSpeechStore(state => state.speech)

  const resetSettings = useSettingsStore(state => state.resetSettings)
  const resetSpeech = useSpeechStore(state => state.resetSpeech)
  const resetStats = useStatsStore(state => state.resetStats)
  const setSettingsState = useSettingsStore.setState
  const setSpeechState = useSpeechStore.setState
  const stats = useStatsStore(state => state.stats)

  const [importStatus, setImportStatus] = useState<string | null>(null)
  const statusTimeoutRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const DEFAULT_TIMER_ON = useMemo(() => TIMER.VALID_VALUES.find(v => v > 0) ?? 15, [])
  const hasAnyTimer = settings.timers.math !== 0 || settings.timers.word !== 0
  const toggleTimers = () => {
    const nextValue = hasAnyTimer ? 0 : DEFAULT_TIMER_ON
    updateTimers({ math: nextValue, word: nextValue })
  }
  const setTimers = (value: TimerOption) => updateTimers({ math: value, word: value })
  const timerOptions = useMemo(
    () =>
      TIMER.VALID_VALUES.map(option => ({
        value: option as TimerOption,
        label: option === 0 ? 'Off' : `${option} sec`,
      })),
    []
  )

  const fontLabels: Record<FontSize, string> = {
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    xlarge: 'Extra Large',
  }

  const themeLabels: Record<ThemeOption, string> = {
    system: 'System',
    light: 'Light',
    dark: 'Dark',
  }

  const themeOptions = useMemo(
    () => [
      { value: 'system' as ThemeOption, label: 'System' },
      { value: 'light' as ThemeOption, label: 'Light' },
      { value: 'dark' as ThemeOption, label: 'Dark' },
    ],
    []
  )

  const handleExport = () => {
    const payload = {
      settings,
      speech: {
        ...speech,
        key: speech.key ? MASKED_API_KEY_PLACEHOLDER : '',
      },
      stats,
    }
    exportData(payload, 'practice-settings.json')
    setImportStatus('Exported practice settings.')
  }

  const applyImportedStats = (incoming: Partial<Stats>) => {
    const safeSolved = Number.isFinite(incoming.solved) ? (incoming.solved as number) : 0
    const safeBestStreak = Number.isFinite(incoming.bestStreak)
      ? (incoming.bestStreak as number)
      : 0
    useStatsStore.setState({ stats: { solved: safeSolved, bestStreak: safeBestStreak } })
  }

  const handleImportFile = async (file: File) => {
    try {
      const data = (await importData(file)) as Record<string, unknown>
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid file')
      }

      if ('settings' in data) {
        const sanitized = sanitizeLoadedSettings((data as { settings: unknown }).settings)
        setSettingsState({ settings: sanitized })
      }
      if ('speech' in data) {
        const sanitizedSpeech = sanitizeLoadedSpeech((data as { speech: unknown }).speech)
        setSpeechState({ speech: sanitizedSpeech })
      }
      if ('stats' in data) {
        applyImportedStats((data as { stats: Partial<Stats> }).stats)
      }
      setImportStatus('Imported settings successfully.')
    } catch (error) {
      console.error(error)
      setImportStatus('Import failed. Please use a valid settings file.')
    }
  }

  const handleImport = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleImportFile(file)
    }
    event.target.value = ''
  }

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      resetSettings()
      resetSpeech()
      resetStats()
      setImportStatus('Settings reset to defaults.')
    }
  }

  useEffect(() => {
    if (!importStatus) {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current)
        statusTimeoutRef.current = null
      }
      return
    }

    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current)
    }

    statusTimeoutRef.current = window.setTimeout(() => {
      setImportStatus(null)
      statusTimeoutRef.current = null
    }, IMPORT_STATUS_TIMEOUT_MS)

    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current)
        statusTimeoutRef.current = null
      }
    }
  }, [importStatus])

  return (
    <details
      className="config__section"
      open={isOpen}
      onToggle={e => onToggle(e.currentTarget.open)}
    >
      <summary className="config__section-summary">
        <span className="config__section-title">General View</span>
      </summary>
      <fieldset className="config__group config__group--framed">
        <legend>Display options</legend>
        <p className="config__hint config__hint--spaced">
          Adjust text size, timers, and data tools.
        </p>
        <div className="config__operations-grid">
          <div className="config__operation-tile">
            {expandedSection !== 'theme' ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedSection('theme')}
              >
                <span className="config__operation-collapsed-label">Theme</span>
                <span className="config__operation-collapsed-value">
                  {themeLabels[settings.theme]}
                </span>
              </button>
            ) : null}

            {expandedSection === 'theme' ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Theme</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Mode"
                    ariaLabel="Color theme"
                    value={settings.theme}
                    options={themeOptions}
                    onChange={value => updateTheme(value as ThemeOption)}
                    icon="◐"
                  />
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedSection(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="config__operation-tile">
            {expandedSection !== 'question' ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedSection('question')}
              >
                <span className="config__operation-collapsed-label">Question Text</span>
                <span className="config__operation-collapsed-value">
                  {fontLabels[settings.fonts.question]}
                </span>
              </button>
            ) : null}

            {expandedSection === 'question' ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Question Text</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Size"
                    ariaLabel="Question text size"
                    value={settings.fonts.question}
                    options={FONT_SIZE_OPTIONS.map(size => ({
                      value: size,
                      label: fontLabels[size as FontSize],
                    }))}
                    onChange={value => updateFontSizes({ question: value as FontSize })}
                    icon="Aa"
                  />
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedSection(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="config__operation-tile">
            {expandedSection !== 'answer' ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedSection('answer')}
              >
                <span className="config__operation-collapsed-label">Answer Box</span>
                <span className="config__operation-collapsed-value">
                  {fontLabels[settings.fonts.answer]}
                </span>
              </button>
            ) : null}

            {expandedSection === 'answer' ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Answer Box</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Size"
                    ariaLabel="Answer text size"
                    value={settings.fonts.answer}
                    options={FONT_SIZE_OPTIONS.map(size => ({
                      value: size,
                      label: fontLabels[size as FontSize],
                    }))}
                    onChange={value => updateFontSizes({ answer: value as FontSize })}
                    icon="A+"
                  />
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedSection(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="config__section-divider" role="presentation" />

        <div className="config__operations-grid">
          <div className="config__operation-tile">
            {!expandedTimers ? (
              <div className={`config__operation-collapsed ${!hasAnyTimer ? 'config__operation-collapsed--disabled' : ''}`}>
                <label className="config__operation-toggle">
                  <input
                    type="checkbox"
                    checked={hasAnyTimer}
                    onChange={e => {
                      toggleTimers()
                      if (!e.target.checked) {
                        setExpandedTimers(false)
                      }
                    }}
                    className="config__operation-toggle-input"
                  />
                  <span className="config__operation-toggle-slider"></span>
                </label>
                <button
                  type="button"
                  className="config__operation-collapsed-content"
                  onClick={() => {
                    if (hasAnyTimer) {
                      setExpandedTimers(true)
                    }
                  }}
                  disabled={!hasAnyTimer}
                >
                  <span className="config__operation-collapsed-label">Timers</span>
                  <span className="config__operation-collapsed-value">
                    {hasAnyTimer ? `${settings.timers.math || settings.timers.word} sec` : 'Off'}
                  </span>
                </button>
              </div>
            ) : null}

            {expandedTimers ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Timers</span>
                </div>
                <div className="config__operation-body">
                  <PillSelect
                    label="Duration"
                    ariaLabel="Timer duration"
                    value={settings.timers.math || settings.timers.word}
                    options={timerOptions}
                    onChange={value => setTimers(value as TimerOption)}
                    icon="⏱"
                  />
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedTimers(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={`config__operation-collapsed ${!settings.celebrations.confetti ? 'config__operation-collapsed--disabled' : ''}`}
          >
            <label className="config__operation-toggle">
              <input
                type="checkbox"
                checked={settings.celebrations.confetti}
                onChange={e => updateCelebrations({ confetti: e.target.checked })}
                className="config__operation-toggle-input"
              />
              <span className="config__operation-toggle-slider"></span>
            </label>
            <div className="config__operation-collapsed-content config__operation-content--static">
              <span className="config__operation-collapsed-label">Confetti</span>
              <span className="config__operation-collapsed-value">
                {settings.celebrations.confetti ? 'On' : 'Off'}
              </span>
            </div>
          </div>
        </div>

        <div className="config__section-divider" role="presentation" />

        <div className="config__operations-grid">
          <div className="config__operation-tile">
            {!expandedData ? (
              <button
                type="button"
                className="config__operation-collapsed"
                onClick={() => setExpandedData(true)}
              >
                <span className="config__operation-collapsed-label">Import / Export</span>
                <span className="config__operation-collapsed-value">💾</span>
              </button>
            ) : null}

            {expandedData ? (
              <div className="config__operation-expanded">
                <div className="config__operation-header">
                  <span className="config__operation-label">Import / Export</span>
                </div>
                <div className="config__operation-body config__operation-body--stacked">
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => {
                      handleExport()
                      setExpandedData(false)
                    }}
                  >
                    Export Settings 💾
                  </button>
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => {
                      handleImport()
                      setExpandedData(false)
                    }}
                  >
                    Import Settings 📥
                  </button>
                  <button
                    type="button"
                    className="config__operation-collapse-btn"
                    onClick={() => setExpandedData(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className="config__operation-collapsed"
            onClick={handleReset}
          >
            <span className="config__operation-collapsed-label">Reset to Defaults</span>
            <span className="config__operation-collapsed-value">🔄</span>
          </button>
        </div>

        <p className="config__hint config__hint--center config__hint--top-gap">
          Need to reset everything? This clears settings, speech, and stats.
        </p>

        {importStatus && (
          <p className="config__hint config__hint--center config__hint--top-gap">
            {importStatus}
          </p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="config__file-input"
          onChange={handleFileChange}
          hidden
        />
      </fieldset>
    </details>
  )
}
